/**
 * The fan's view.
 *
 * Ordered by what someone actually opens this to find out: how much have I got,
 * where can I earn more, is my card set up. Everything else sits below that.
 *
 * The tone is plain rather than promotional. This product asks people to attach
 * a payment card to it, and the way to deserve that is to say exactly what is
 * happening — including that pending money is pending because the purchase can
 * still be refunded.
 */
import { useState } from 'react';
import {
  useBeginCardEnrolment,
  useCards,
  useCashback,
  useConnect,
  useEndConnection,
  useMemberConnections,
  useOffers,
  useRemoveCard,
  useTransactions,
} from '../api/hooks.js';
import { Money, Stat } from './Money.js';
import { ActivityTable, ErrorNote, Panel } from './shared.js';
import { CashOutPanel } from './CashOutPanel.js';
import { DestinationsPanel } from './DestinationsPanel.js';
import { StatementsPanel } from './StatementsPanel.js';
import { formatRateBps, monogramFor, tileColourFor } from '@kickback/contracts/brand';
import type { OfferWire } from '../api/client.js';

export function MemberDashboard() {
  const cashback = useCashback(true);
  const connections = useMemberConnections(true);

  const hasCreator = (connections.data ?? []).some((c) => c.effectiveTo === null);

  return (
    <div className="stack">
      <Panel title="Your cashback" subtitle="Earned on what you were going to buy anyway.">
        {cashback.isError && <ErrorNote error={cashback.error} />}
        {/* `--lead` sets the first figure at display size. A fan opening this
            page is asking one question — how much can I spend — and four equal
            numbers make them find the answer among the others. The order below
            is the order it is asked in, and the CSS reads it rather than
            taking a second prop that could disagree with it. */}
        <div className="stats stats--lead">
          <Stat
            label="Ready to spend"
            value={cashback.data?.available}
            tone="positive"
            hint="Yours now."
          />
          <Stat
            label="On the way"
            value={cashback.data?.pending}
            hint="Held until the shop's return window closes."
          />
          <Stat label="Earned all time" value={cashback.data?.lifetimeEarned} tone="muted" />
        </div>
      </Panel>

      {/* Directly under the balance, and above everything else. Getting the
          money out is the point of having earned it, and burying it below the
          shop list would be the product quietly hoping nobody asks. */}
      <CashOutPanel />
      {/* Directly under it, because the only reason to open this is that the
          cash out above needs somewhere to go. Above the shop list for the same
          reason: setup that blocks the money comes before browsing. */}
      <DestinationsPanel />

      {/* Below the two panels that move money and above the shop list, which
          is where the creator dashboard puts it too. A statement is a record
          of what already happened: it belongs after the things a fan came here
          to do, and before browsing. */}
      <StatementsPanel kind="member" enabled />

      <OffersPanel connected={hasCreator} />
      <CardPanel />
      <CreatorPanel />

      <Panel title="Your activity" subtitle="Every movement, exactly as recorded.">
        <MemberActivity />
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function OffersPanel({ connected }: { connected: boolean }) {
  const offers = useOffers(true);
  const rows = offers.data ?? [];

  return (
    <Panel
      title="Where you can earn"
      /* Not "follow a creator to start earning", which is what this said and
         was never true of the product we meant to build — and, until migration
         0026, was true of the one we had. A fan earns the same rate either way;
         a creator is someone they may want to support, not a gate. */
      subtitle={
        connected
          ? 'Your rate at each shop, based on the creator you follow.'
          : 'Your rate at each shop. No creator needed.'
      }
    >
      {offers.isError && <ErrorNote error={offers.error} />}
      {offers.isPending && <p className="empty">Loading…</p>}

      {!offers.isPending && rows.length === 0 && (
        <p className="empty">No shops yet. This fills up as merchants join.</p>
      )}

      {rows.length > 0 && (
        <ul className="shop-grid">
          {rows.map((offer) => (
            <OfferTile key={offer.id} offer={offer} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * One shop.
 *
 * The percentage leads, because that is the number someone is scanning for and
 * "$4.50 back per $100" is the answer to a question they have not asked yet. It
 * stays underneath, since a rate is a promise and a worked example is what
 * makes the promise concrete.
 */
function OfferTile({ offer }: { offer: OfferWire }) {
  return (
    <li className="shop-tile">
      <Mark name={offer.merchantName} slug={offer.merchantSlug} logoUrl={offer.logoUrl} />

      <span className="shop-tile__merchant">{offer.merchantName}</span>

      {offer.cashbackBps === null ? (
        /* Only a creator browsing the fan catalog reaches this. Every member
           holds the house connection from account creation (migration 0026), so
           a fan always has a rate. */
        <span className="shop-tile__rate--none">Shown on your fan account</span>
      ) : (
        <span className="shop-tile__rate">
          <span className="shop-tile__percent">{formatRateBps(offer.cashbackBps)}</span>
          <span className="shop-tile__per">
            <Money value={offer.cashbackOnHundred ?? undefined} /> back per $100
          </span>
          {offer.creatorDisplayName !== null && (
            <span className="shop-tile__via">via {offer.creatorDisplayName}</span>
          )}
        </span>
      )}
    </li>
  );
}

/**
 * The merchant's mark: their logo where we have one, their initials where we do
 * not.
 *
 * Both are the same 48px square, so the day a catalog sync starts filling
 * `logoUrl` the grid does not move. The colour comes from `tileColourFor`,
 * shared with the app so one merchant is one colour in both.
 */
function Mark({
  name,
  slug,
  logoUrl,
}: {
  name: string;
  slug: string;
  logoUrl: string | null | undefined;
}) {
  // Truthiness, not `!== null`. An API that omits the field, or sends an empty
  // string, would otherwise reach <img src={undefined}> and draw a broken
  // image where a perfectly good monogram belongs.
  if (logoUrl !== null && logoUrl !== undefined && logoUrl !== '') {
    return (
      <img
        className="shop-tile__logo"
        src={logoUrl}
        // The name is already rendered directly below, so announcing it again
        // here would have a screen reader say every shop twice.
        alt=""
        loading="lazy"
        width={48}
        height={48}
      />
    );
  }

  return (
    <span
      className="shop-tile__mark"
      style={{ background: tileColourFor(slug) }}
      aria-hidden="true"
    >
      {monogramFor(name)}
    </span>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Card enrolment.
 *
 * Two steps, and the second is not ours: we mint a reference, the provider's
 * SDK takes the card details, and the card appears once they confirm it. The UI
 * says so rather than showing a spinner implying we are waiting on ourselves —
 * the browser genuinely cannot know the outcome.
 */
function CardPanel() {
  const cards = useCards(true);
  const begin = useBeginCardEnrolment();
  const remove = useRemoveCard();
  const [started, setStarted] = useState(false);

  const rows = cards.data ?? [];

  return (
    <Panel
      title="Your card"
      subtitle="Tap it as usual. Cashback finds you."
      action={
        rows.length === 0 && !started ? (
          <button
            className="button"
            onClick={() => begin.mutate(undefined, { onSuccess: () => setStarted(true) })}
            disabled={begin.isPending}
          >
            {begin.isPending ? 'Starting…' : 'Add a card'}
          </button>
        ) : undefined
      }
    >
      {begin.isError && <ErrorNote error={begin.error} />}
      {remove.isError && <ErrorNote error={remove.error} />}

      <p className="note">
        Your card number never reaches us. It goes straight to our card network
        partner, who tell us only the last four digits.
      </p>

      {started && rows.length === 0 && (
        <div className="pending-note">
          <strong>Waiting for the card network to confirm.</strong>
          <span>
            Your card will appear here on its own — you can leave this page.
          </span>
        </div>
      )}

      {cards.isPending && <p className="empty">Loading…</p>}

      {!cards.isPending && rows.length === 0 && !started && (
        <p className="empty">No card yet. Add one to start earning.</p>
      )}

      {rows.length > 0 && (
        <ul className="cards">
          {rows.map((card) => (
            <li key={card.id} className="card-row">
              <span className="card-row__scheme">{card.scheme}</span>
              <span className="card-row__digits">•••• {card.lastFour}</span>
              <span className="card-row__expiry">
                {String(card.expiryMonth).padStart(2, '0')}/{String(card.expiryYear).slice(-2)}
              </span>
              <button
                className="button button--quiet"
                onClick={() => remove.mutate(card.id)}
                disabled={remove.isPending}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

function CreatorPanel() {
  const connections = useMemberConnections(true);
  const connect = useConnect();
  const end = useEndConnection();
  const [code, setCode] = useState('');

  const active = (connections.data ?? []).filter((c) => c.effectiveTo === null);

  return (
    <Panel
      title="Your creator"
      subtitle={
        active.length === 0
          ? 'Optional. Adding one costs you nothing — their share comes out of ours, not your cashback.'
          : "They get a share of the shop's commission — never a share of your cashback."
      }
    >
      {connect.isError && <ErrorNote error={connect.error} />}
      {end.isError && <ErrorNote error={end.error} />}

      {active.length === 0 ? (
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = code.trim();
            if (trimmed.length > 0) connect.mutate(trimmed, { onSuccess: () => setCode('') });
          }}
        >
          <label className="field">
            <span className="field__label">Creator code</span>
            <input
              className="input"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="the code from their video or bio"
              autoComplete="off"
            />
          </label>
          <button className="button" type="submit" disabled={connect.isPending}>
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </button>
        </form>
      ) : (
        <ul className="connections">
          {active.map((connection) => (
            <li key={connection.id} className="connection">
              <div>
                <span className="connection__name">
                  {connection.creatorHandle ?? 'Your creator'}
                </span>
                <span className="connection__terms">
                  You keep {Math.round(connection.terms.memberBps / 100)}% of every commission
                </span>
              </div>
              <button
                className="button button--quiet"
                onClick={() => end.mutate(connection.id)}
                disabled={end.isPending}
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function MemberActivity() {
  const transactions = useTransactions(true);
  if (transactions.isError) return <ErrorNote error={transactions.error} />;
  return <ActivityTable rows={transactions.data?.data} loading={transactions.isPending} />;
}
